import { Card, CardContent } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { useToast } from "@/shared/hooks/use-toast";
import { CreditCard, Store, DollarSign, CheckCircle, XCircle, Copy } from "lucide-react";
import { useTranslation } from "@/shared/i18n";

interface Plan {
  id?: string;
  active?: boolean;
  price?: number;
  store?: 'REVENUE_CAT' | 'APPLE' | 'GOOGLE';
}

interface PlanFieldProps {
  plan?: Plan;
}

const getStoreLabels = (t: (key: string) => string) => ({
  REVENUE_CAT: t('revenueCat'),
  APPLE: t('appleStore'),
  GOOGLE: t('googlePlay'),
});

const storeColors = {
  REVENUE_CAT: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  APPLE: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  GOOGLE: 'bg-green-500/20 text-green-400 border-green-500/30',
};

export function PlanField({ plan }: PlanFieldProps) {
  const { t } = useTranslation('profiles');
  const { toast } = useToast();
  const storeLabels = getStoreLabels(t);

  const handleCopy = async (value: string | undefined, label: string) => {
    if (!value) return;
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(value);
        toast({
          title: t('copied'),
          description: `${label} ${t('planIdCopied')}`,
        });
      } else {
        throw new Error(t('clipboardUnavailable'));
      }
    } catch {
      toast({
        title: t('copyError'),
        description: t('copyErrorDescription'),
        variant: "destructive",
      });
    }
  };

  const formatPrice = (price?: number) => {
    if (price === undefined || Number.isNaN(price)) return "—";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(price);
  };

  if (!plan) {
    return (
      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <CreditCard className="w-5 h-5 text-gray-400" />
          <span className="text-base font-medium text-white">{t('plan')}</span>
        </div>
        <Card className="bg-[#1A1A1A] border-[#333333]">
          <CardContent className="p-4">
            <p className="text-sm text-gray-400">{t('noPlanAssociated')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center space-x-2">
        <CreditCard className="w-5 h-5 text-white" />
        <span className="text-base font-medium text-white">{t('plan')}</span>
      </div>
      <Card className="bg-[#1A1A1A] border-[#333333]">
        <CardContent className="p-4 space-y-3">
          {/* Status do Plano */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400">{t('planStatus')}</span>
            <Badge
              variant={plan.active ? "default" : "secondary"}
              className={
                plan.active
                  ? "bg-green-500/20 text-green-400 border-green-500/30"
                  : "bg-red-500/20 text-red-400 border-red-500/30"
              }
            >
              {plan.active ? (
                <>
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {t('active')}
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3 mr-1" />
                  {t('inactive')}
                </>
              )}
            </Badge>
          </div>

          {/* Preço */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-400 flex items-center">
              <DollarSign className="w-4 h-4 mr-1" />
              {t('planPrice')}
            </span>
            <span className="text-sm font-semibold text-white">
              {formatPrice(plan.price)}
            </span>
          </div>

          {/* Loja */}
          {plan.store && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400 flex items-center">
                <Store className="w-4 h-4 mr-1" />
                {t('planStore')}
              </span>
              <Badge
                variant="outline"
                className={storeColors[plan.store]}
              >
                {storeLabels[plan.store]}
              </Badge>
            </div>
          )}

          {/* ID do Plano */}
          <div className="flex items-center justify-between pt-2 border-t border-[#333333] gap-2">
            <span className="text-xs text-gray-500">{t('planId')}</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 font-mono truncate max-w-[180px]" title={plan.id}>
                {plan.id ?? "—"}
              </span>
              {plan.id && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-gray-300 hover:text-white"
                  onClick={() => handleCopy(plan.id, t('planId'))}
                >
                  <Copy className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
